import {
	DropDownListChangeEvent,
	MultiSelect,
} from "@progress/kendo-react-dropdowns";
// import { adminUpdateUserApi } from "api/administration";
import {
	changePhoneNumberApi,
	changePhoneNumberVerificationApi,
} from "api/auth";
import { allUsersGroupRolesApi, updateTwoFactorSendTypeApi } from "api/user";
import { t } from "i18next";
import { CustomError } from "interface";
import { IUserRole, UserProfile } from "interface/user";
import React, { FC, useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router";
import { useInfoStore } from "store/error";
import { useToastStore } from "store/toast";
import CustomButton, { TextButton } from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import Info from "ui/Info";
import { CustomInput } from "ui/Input";
import { COLOR } from "utils/constant";
import { NOTIFICATION_METHOD_DROPDOWN } from "utils/dropdown/notification";

interface DataResponse {
	message: string;
	expiresIn: string;
	attemptsRemaining?: number;
	codeLength: number;
}

export const UserInformation: FC<{
	user: UserProfile;
	setUser: (user: UserProfile) => void;
	countryCodes: string[];
	setCountryCode: (countryCodes: string) => void;
	adminPage?: boolean;
}> = ({ user, setUser, countryCodes, setCountryCode, adminPage = false }) => {
	const [phoneNumber, setPhoneNumber] = React.useState({
		number: user.mobileNumber || "",
		prefix: user.mobilePrefix || "+47",
	});
	const [twoFaCode, setTwoFaCode] = React.useState("");
	const [twoFaCodeError, setTwoFaCodeError] = React.useState("");
	const [phoneNumberError, setPhoneNumberError] = React.useState("");
	const [twoFaCodeSent, setTwoFaCodeSent] = React.useState(false);

	const { setError, setInfo, resetStore } = useInfoStore();

	const [response, setResponse] = useState<DataResponse | null>(null);
	const [countdown, setCountdown] = useState<number>(0);
	const [userGroups, setUserGroups] = useState<IUserRole[]>([]);
	// let { path, id, action } = useParams();

	// const navigatve = useNavigate();
	const { showError, showSuccess } = useToastStore();

	useEffect(() => {
		if (response && response.expiresIn) {
			const expireTime = new Date(response.expiresIn).getTime();
			const interval = setInterval(() => {
				const now = new Date().getTime();
				const diff = Math.max(expireTime - now, 0);
				setCountdown(Math.floor(diff / 1000));
				if (diff === 0) clearInterval(interval);
			}, 1000);
			return () => clearInterval(interval);
		}
	}, [response]);

	useEffect(() => {
		allUsersGroupRolesApi()
			.then((res) => {
				setUserGroups(res.data);
			})
			.catch((error) => {
				const err = new CustomError(
					t("UserGroupFetchFailed", "UserGroupFetchFailed")
				);
				const showCustomError = true;
				setError(err, showCustomError);
			});
	}, []);

	const [showConfirmationModal, setShowConfirmationModal] =
		React.useState(false);

	function handleCountryCodeChange(event: DropDownListChangeEvent): void {
		const newCountryCode = event.value as string;
		setCountryCode(newCountryCode);
	}

	const handlePhoneNumberChange = (value: string): void => {
		if ((value === "" || /^[0-9]*$/.test(value)) && value.length <= 8) {
			setPhoneNumber({ number: value, prefix: phoneNumber.prefix });
		}
		if (adminPage) {
			setUser({
				...user,
				mobilePrefix: phoneNumber.prefix,
				mobileNumber: value,
			});
		}
	};

	const handleSaveNumber = () => {
		if (user.mobileNumber === phoneNumber.number) {
			setPhoneNumberError("Phone number is same as existing number");
			return;
		}

		const isValidPhoneNumber = /^[0-9]{8}$/.test(phoneNumber.number);
		if (isValidPhoneNumber) {
			setPhoneNumberError("");
		} else {
			setPhoneNumberError(t("PhoneNumberInvalid"));
			return;
		}
		setShowConfirmationModal(true);
	};

	const confirmChangePhone = () => {
		setCountdown(0);

		changePhoneNumberApi({
			phoneNumber: phoneNumber.number,
			prefix: phoneNumber.prefix,
		})
			.then((res) => {
				setResponse(res.data);
				setTwoFaCode("");
				setTwoFaCodeSent(true);
			})
			.catch((error) => {
				const err = new CustomError(
					t("PhoneNumberChangeFailed", "PhoneNumberChangeFailed")
				);
				const showCustomError = true;
				setError(err, showCustomError);
				setPhoneNumberError(t("PhoneNumberChangeFailed"));
			})
			.finally(() => {
				setShowConfirmationModal(false);
			});
	};

	const handleVerify2FaCode = () => {
		if (twoFaCode === "") {
			setTwoFaCodeError(t("TwoFaCodeRequired"));
			return;
		}
		changePhoneNumberVerificationApi({
			phoneNumber: phoneNumber.number,
			prefix: phoneNumber.prefix,
			token: twoFaCode,
		})
			.then((response) => {
				// setTwoFaCodeVerified(true);
				// setPhoneNumberVerified(true);
				setInfo({
					title: t("Success"),
					description: t("PhoneNumberChangeSuccess"),
					show: true,
					actions: [
						{
							text: t("Ok"),
							btn: () => {
								handleCancelVerification();
								resetStore();
							},
							textColor: COLOR.StatusGreen,
						},
					],
				});
				setTwoFaCodeSent(false);
				// setTwoFaCodeError("");
			})
			.catch((error) => {
				// if(error.status === 403) {

				// }
				const err = new CustomError(
					t("PhoneNumberChangeFailed", "PhoneNumberChangeFailed")
				);
				const showCustomError = true;
				setError(err, showCustomError);
				setTwoFaCodeError(t("PhoneNumberChangeFailed"));
			});
		setTwoFaCodeError("");
	};

	const handleTwoFaTypeUpdate = (value: { id: number; name: string }) => {
		updateTwoFactorSendTypeApi(value)
			.then((res) => {
				showSuccess(`${t("Saved")} ${t("Success")}`, { duration: 2000 });
				setUser({
					...user,
					twoFactorSendType: {
						id: value.id,
						name: value.name,
						nameEnglish: "",
					},
				});
			})
			.catch((error) => {
				showError(`${t("Saved")} ${t("Failed")}`, { duration: 2000 });
			});
	};
	const handleCancelVerification = () => {
		setTwoFaCodeSent(false);
		setTwoFaCodeError("");
		setPhoneNumberError("");
	};

	return (
		<div
			style={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				gap: "30px",
			}}
		>
			<div
				style={{
					display: "flex",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<CustomInput
					inputStyle={{ width: "100%" }}
					label={t("Email")}
					disabled={adminPage ? false : true}
					value={user.email || ""}
					onChange={(value) => {
						setUser({ ...user, email: value });
					}}
				/>
				<MultiSelect
					style={{ width: "100%", borderColor: COLOR.Green }}
					data={userGroups}
					textField="name"
					dataItemKey="id"
					label={t("UserGroup")}
					disabled={adminPage ? false : true}
					placeholder={t("Select") + "  " + t("UserGroup")}
					onChange={(e) => {
						const selectedGroups = e.value as IUserRole[];
						setUser({
							...user,
							userGroup: selectedGroups,
						});
					}}
					value={user?.userGroup || []}
				/>
			</div>
			<div
				style={{
					display: "flex",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<CustomInput
					inputStyle={{ width: "100%" }}
					label={t("FirstName")}
					value={user.firstName || ""}
					disabled={adminPage ? false : true}
					onChange={(value) => {
						setUser({ ...user, firstName: value });
					}}
				/>
				<CustomInput
					inputStyle={{ width: "100%" }}
					label={t("LastName")}
					value={user.lastName || ""}
					disabled={adminPage ? false : true}
					onChange={(value) => {
						setUser({ ...user, lastName: value });
					}}
				/>
			</div>
			<div
				style={{
					display: "flex",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<div style={{ width: "100%" }}>
					<span style={{ color: COLOR.StatusRed }}>{phoneNumberError}</span>

					<div style={{ display: "flex", gap: "20px", width: "100%" }}>
						<CustomDropDownList
							style={{ width: "300px" }}
							data={countryCodes}
							dataItemKey=""
							textField=""
							label={t("CountryCode")}
							value={phoneNumber.prefix}
							onChange={(value) =>
								setPhoneNumber({ ...phoneNumber, prefix: value.value })
							}
						/>
						<div>
							<CustomInput
								inputStyle={{ width: "100%" }}
								label={t("MobileNumber")}
								placeholder={t("MobileNumber")}
								value={phoneNumber.number || ""}
								isError={phoneNumberError !== ""}
								disabled={twoFaCodeSent}
								onChange={handlePhoneNumberChange}
							/>
						</div>
					</div>
				</div>

				<CustomDropDownList
					style={{ width: "100%" }}
					data={NOTIFICATION_METHOD_DROPDOWN}
					dataItemKey="id"
					textField="name"
					label={t("TwoFactorSendMedium")}
					value={user.twoFactorSendType}
					onChange={(value) => {
						handleTwoFaTypeUpdate(value.value);
						// setUser({
						// 	...user,
						// 	twoFactorSendType: {
						// 		id: value.value.id,
						// 		name: value.value.name,
						// 		nameEnglish: "",
						// 	},
						// });
					}}
				/>
			</div>

			<div style={{ width: "100%", display: "flex" }}>
				<div style={{ width: "100%", marginTop: "20px" }}>
					{twoFaCodeSent ? (
						<div style={{ marginTop: "40px" }}>
							<h2 style={{ textAlign: "center" }}>{t("Verify")}</h2>
							<div>
								<CustomInput
									inputStyle={{ width: "50%" }}
									label={t("TwoFactorCode")}
									placeholder={t("TwoFactorCode")}
									value={twoFaCode || ""}
									onChange={(value) => setTwoFaCode(value)}
								/>
								<div
									style={{
										marginTop: "20px",
										display: "flex",
										flexDirection: "row",
										alignItems: "center",
									}}
								>
									<span>
										{t("TwFactorCodeExpiresIn")}: {countdown} seconds
									</span>
									<TextButton
										text={t("ResendCode")}
										textStyle={{
											color: COLOR.Green,
											marginLeft: "10px",
											marginTop: "3px",
										}}
										onClick={confirmChangePhone}
									/>
								</div>
								<div
									style={{
										width: "40%",
										marginTop: "20px",
										display: "flex",
										justifyContent: "space-between",
									}}
								>
									<CustomButton
										btnStyle={{ width: "20%" }}
										label={t("Cancel")}
										bgColor={COLOR.Orange}
										labelStyle={{ color: "white" }}
										onClick={handleCancelVerification}
									/>
									<CustomButton
										btnStyle={{ width: "40%" }}
										label={t("VerifyNow")}
										bgColor={COLOR.Green}
										labelStyle={{ color: "white" }}
										disabled={
											!response ||
											response.attemptsRemaining === 0 ||
											response.codeLength !== twoFaCode.length
										}
										onClick={handleVerify2FaCode}
									/>
								</div>
							</div>
						</div>
					) : (
						<div
							style={{
								marginTop: "20px",
								display: "flex",
								justifyContent: "space-between",
							}}
						>
							<CustomButton
								btnStyle={{
									width: "70%",
									marginTop: "20px",
									maxWidth: "400px",
								}}
								label={t("Save")}
								bgColor={COLOR.Green}
								labelStyle={{ color: "white" }}
								onClick={handleSaveNumber}
							/>
						</div>
					)}
				</div>
				<div style={{ width: "100%" }}></div>
			</div>

			<Info
				title={t("TwoFactorVerification")}
				description={t("PhoneNumberVerifyingMessage")}
				show={showConfirmationModal}
				close={() => setShowConfirmationModal(false)}
				actions={[
					{
						text: t("Cancel"),
						btn: () => setShowConfirmationModal(false),
						textColor: COLOR.StatusRed,
					},
					{
						text: t("VerifyNow"),
						btn: confirmChangePhone,
						textColor: COLOR.StatusGreen,
					},
				]}
			/>
		</div>
	);
};
